<template>
  <div class="previous-session-div" v-if="!showPreviousSessionComponent && !showTextAreaComponent">
    <div class="backicon-div">
      <span class="material-symbols-outlined hover-effect" @click="showPreviousSessionComponentFn()">chevron_left</span>
      <span class="label-span">Back</span>
    </div>
    <div class="button-div">
      <span class="label-span">Discussions</span>
      <Button :label="dynamicDiscussionLabel" outlined size="small" class="custom-placeholder-color"
        @click="showTextAreaComponentFn(dynamicDiscussionLabel, 'Discussions')"></Button>
    </div>
    <div class="button-div">
      <span class="label-span">Clinical Stage</span>
      <Button :label="dynamicClinicalStageLabel" outlined size="small" class="custom-placeholder-color"
        @click="showTextAreaComponentFn(dynamicClinicalStageLabel, 'Clinical Staging')"></Button>
    </div>
    <div class="button-div">
      <span class="label-span">Referrals</span>
      <Button :label="dynamicReferralLabel" outlined size="small" class="custom-placeholder-color"
        @click="showTextAreaComponentFn(dynamicReferralLabel, 'Referrals')"></Button>
    </div>
    <div class="button-div">
      <span class="label-span">Recommendations</span>
      <Button :label="dynamicRecommendationLabel" outlined size="small" class="custom-placeholder-color"
        @click="showTextAreaComponentFn(dynamicRecommendationLabel, 'Recommendations')"></Button>
    </div>
    <div class="button-div">
      <span class="label-span">TNM</span>
      <Button :label="dynamicTNMLabel" outlined size="small" class="custom-placeholder-color"
        @click="showTextAreaComponentFn(dynamicTNMLabel, 'TNM')"></Button>
    </div>
    <div class="button-div">
      <span class="label-span">Notes</span>
      <Button :label="dynamicNotesLabel" outlined size="small" class="custom-placeholder-color"
        @click="showTextAreaComponentFn(dynamicNotesLabel, 'Notes')"></Button>
    </div>
  </div>
  <div v-else-if="showPreviousSessionComponent">
    <TumorBoardPreviousSessions />
  </div>
  <div v-if="showTextAreaComponent">
    <TextAreaComponent :selectedLabel="selectedButtonLabel" :heading="recordHeading"></TextAreaComponent>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, defineProps, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import Button from 'primevue/button'
import TumorBoardPreviousSessions from './TumorBoardPreviousSessions.vue'
import TextAreaComponent from './TextAreaComponent.vue'

const store = useStore()
const previousSessionsDetailsData = ref([])
const dynamicDiscussionLabel = ref('')
const dynamicClinicalStageLabel = ref('')
const dynamicReferralLabel = ref('')
const dynamicRecommendationLabel = ref('')
const dynamicTNMLabel = ref('')
const dynamicNotesLabel = ref('')
const showPreviousSessionComponent = ref(false)
const showTextAreaComponent = ref(false)
const selectedButtonLabel = ref('')
const recordHeading = ref('')
const selectedRecordedPreviousSessionDetails = computed(() => store.state.session.selectedRecordedPreviousSessionDetails)

const props = defineProps({
  previousSessionDetails: {
    type: Object,
    required: true,
  },
})

onMounted(() => {
  previousSessionsDetailsData.value = props.previousSessionDetails || selectedRecordedPreviousSessionDetails.value
  dynamicDiscussionLabel.value = getObjectKeysData(previousSessionsDetailsData.value.recording_info.discussion)
  dynamicClinicalStageLabel.value = getObjectKeysData(previousSessionsDetailsData.value.recording_info.clinical_staging)
  dynamicReferralLabel.value = getObjectKeysData(previousSessionsDetailsData.value.recording_info.referrals)
  dynamicRecommendationLabel.value = getObjectKeysData(previousSessionsDetailsData.value.recording_info.recommendations)
  dynamicTNMLabel.value = previousSessionsDetailsData.value.recording_info.tnm || '---'
  dynamicNotesLabel.value = previousSessionsDetailsData.value.recording_info.notes || '---'
})

/**
 * Returns a string of comma separated keys from the provided object where the corresponding values are truthy.
 * If no keys have truthy values, returns '---'.
 *
 * @param {Object} keysData - The object containing key-value pairs.
 * @returns {string} A comma-separated string of keys or '---'.
 *
 * @example
 * getObjectKeysData({ a: true, b: false, c: true }) // 'a, c'
 */
const getObjectKeysData = (keysData) => {
  const filteredKeys = Object.entries(keysData)
    .filter(([, value]) => value)
    .map(([key]) => key)

  return filteredKeys.length ? filteredKeys.join(', ') : '---'
}

const showPreviousSessionComponentFn = () => showPreviousSessionComponent.value = true

const showTextAreaComponentFn = (selectedLabel, heading) => {
  selectedButtonLabel.value = selectedLabel
  recordHeading.value = heading
  showTextAreaComponent.value = true
}

</script>

<style scoped>
.previous-session-div {
  gap: 20px;
  display: flex;
  flex-direction: column;
  padding-bottom: 2rem;
}

.backicon-div {
  display: flex;
  align-items: center;
  margin-bottom: 2%;
}

.button-div {
  display: grid;
  text-align: start;
  margin-bottom: 2%;
  width: 220px;
}

.hover-effect {
  margin-right: 4%;
  margin-bottom: 4%;
  cursor: pointer;
}

.label-span {
  color: var(--Grey600);
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  margin-bottom: 3%;
}

.custom-placeholder-color ::placeholder {
  overflow: hidden;
  color: var(--Black);
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.24px;
  white-space: nowrap;
}
</style>

<style>
.button-div .p-button.p-button-outlined:hover {
  color: var(--Primary);
}

.button-div .p-button-label {
  max-width: 235px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 400;
  color: var(--Grey700);
  text-align: start;
}

.button-div .p-button-outlined {
  border: 1px solid var(--Grey400);
}
</style>
